import { TextField } from '@mui/material';
import type { ChangeEvent, ClipboardEvent, ReactNode, Ref } from 'react';

export const OTP_CODE_LENGTH = 6;

const OTP_CODE_PATTERN = new RegExp(`^[0-9]{${OTP_CODE_LENGTH}}$`);

export const normalizeOtpCode = (value: string) => value.replace(/[^0-9]/g, '').slice(0, OTP_CODE_LENGTH);

export const isValidOtpCode = (value: string) => OTP_CODE_PATTERN.test(value);

type OtpCodeFieldProps = {
  autoFocus?: boolean;
  disabled?: boolean;
  error?: boolean;
  helperText?: ReactNode;
  inputRef?: Ref<HTMLInputElement>;
  label: ReactNode;
  name: string;
  onValueChange: (value: string) => void;
  value: string;
};

const OtpCodeField = ({
  autoFocus = false,
  disabled = false,
  error = false,
  helperText,
  inputRef,
  label,
  name,
  onValueChange,
  value,
}: OtpCodeFieldProps) => {
  const isComplete = isValidOtpCode(value);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    onValueChange(normalizeOtpCode(event.target.value));
  };

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    onValueChange(normalizeOtpCode(event.clipboardData.getData('text')));
  };

  return (
    <TextField
      autoComplete="one-time-code"
      autoFocus={autoFocus}
      color={isComplete && !error ? 'success' : 'primary'}
      disabled={disabled}
      error={error}
      fullWidth
      helperText={helperText ?? `${value.length}/${OTP_CODE_LENGTH}`}
      inputRef={inputRef}
      label={label}
      margin="normal"
      name={name}
      onChange={handleChange}
      required
      slotProps={{
        formHelperText: {
          sx: { minHeight: 20, mx: 0, textAlign: 'right' },
        },
        htmlInput: {
          autoCapitalize: 'none',
          autoCorrect: 'off',
          enterKeyHint: 'done',
          inputMode: 'numeric',
          maxLength: OTP_CODE_LENGTH,
          minLength: OTP_CODE_LENGTH,
          onPaste: handlePaste,
          pattern: `[0-9]{${OTP_CODE_LENGTH}}`,
          spellCheck: false,
        },
      }}
      sx={{
        '& .MuiInputBase-input': {
          fontSize: '1.35rem',
          fontVariantNumeric: 'tabular-nums',
          fontWeight: 600,
          letterSpacing: '0.4em',
          textAlign: 'center',
          textIndent: '0.4em',
        },
      }}
      type="text"
      value={value}
    />
  );
};

export default OtpCodeField;
